import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Database, Globe2, LineChart, PenTool, Stethoscope, Lock, Landmark, Scale, MessageSquareText, UserRound } from "lucide-react";
import { Seo } from "../components/Seo";
import { HeroGraphic } from "../components/HeroGraphic";
import { PublicationCard } from "../components/PublicationCard";
import { SERVICES, DEPARTMENTS, ENGAGEMENT_STEPS } from "../data/services";
import { STAGES } from "../data/lifecycle";
import { useSettings } from "../hooks/useSettings";
import { api } from "../lib/api";

const ICONS = { heor: BarChart3, "us-market-access": Landmark, "eu-jca": Scale, rwe: Database, "social-listening": MessageSquareText, "hcp-intelligence": UserRound, "medical-affairs": Stethoscope, "competitive-intelligence": Globe2, "advanced-analytics": LineChart, "scientific-communications": PenTool };

export default function Home() {
  const settings = useSettings();
  const [pubs, setPubs] = useState([]);
  const [pubTotal, setPubTotal] = useState(0);

  useEffect(() => {
    api.get("/publications", { params: { limit: 4 } }).then((r) => { setPubs(r.data.items); setPubTotal(r.data.total); }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo description="MarksMan Healthcare is a strategic HEOR, Real World Evidence and Medical Affairs consultancy helping pharma, biotech and medtech teams design evidence strategy — and generate the evidence behind it." jsonLd={{ "@context": "https://schema.org", "@type": "Organization", name: "MarksMan Healthcare Communications Pvt. Ltd.", url: window.location.origin, sameAs: settings?.linkedin ? [settings.linkedin] : [] }} />

      <section className="grain relative overflow-hidden bg-[var(--navy)] text-white">
        <div className="grid-lines absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-32">
          <div className="lg:col-span-5">
            <div className="eyebrow rise !text-teal-300 !text-sm !tracking-[0.3em]">Evidence. Insights. Access.</div>
            <h1 className="rise rise-1 mt-6 text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-[3rem]">Evidence strategy for every stage of the product lifecycle.</h1>
            <p className="rise rise-2 mt-6 max-w-xl text-lg leading-relaxed text-slate-300">From first-in-human to loss of exclusivity, MarksMan works alongside Medical Affairs, Market Access, Commercial and BD leaders to decide what evidence each stage demands — anticipating HTA, JCA, CMS and payer expectations — and then to build it.</p>
            <div className="rise rise-3 mt-10 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary">Book a strategy conversation <ArrowRight size={16} /></Link>
              <a href="#lifecycle" data-testid="hero-cta-secondary" className="btn-outline !border-white/40 !text-white hover:!bg-white hover:!text-[var(--navy)]">Where are you in the lifecycle?</a>
            </div>
          </div>
          <div className="rise rise-3 flex items-center self-center lg:col-span-7">
            <HeroGraphic />
          </div>
        </div>
      </section>

      <section id="lifecycle" className="border-b border-slate-200" data-testid="home-lifecycle">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-3xl">
            <div className="eyebrow">Where are you in the lifecycle?</div>
            <h2 className="mt-3 text-2xl font-bold text-[var(--navy)] sm:text-3xl lg:text-4xl">Every stage asks a different evidence question. We answer the one in front of you — and anticipate the next.</h2>
          </div>
          <div className="mt-12 grid gap-3 md:grid-cols-5">
            {STAGES.map((s, i) => (
              <div key={s.slug} data-testid={`stage-card-${s.slug}`} className="card relative flex flex-col !p-5">
                <div className="flex items-center justify-between"><span className="font-mono text-xs font-bold text-[var(--teal)]">0{i + 1}</span><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{s.phase}</span></div>
                <h3 className="mt-4 text-lg font-bold leading-snug text-[var(--navy)]">{s.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.short}</p>
                <p className="mt-4 border-t border-slate-100 pt-3 text-xs italic leading-relaxed text-slate-500">“{s.questions[0]}”</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="home-departments">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="eyebrow">Who we advise</div>
            <h2 className="mt-3 text-2xl font-bold text-[var(--navy)] sm:text-3xl lg:text-4xl">Strategy for the departments that own the evidence</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">We don't wait for a brief. We work with functional leaders to define the questions worth answering, then bring the multidisciplinary team — physicians, health economists, statisticians, medical writers — to answer them.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:col-span-8">
            {DEPARTMENTS.map((d) => (
              <div key={d.name} data-testid={`department-card-${d.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="card">
                <h3 className="text-lg font-semibold text-[var(--navy)]">{d.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{d.need}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-[var(--surface)]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="eyebrow">How we engage</div>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold text-[var(--navy)] sm:text-3xl lg:text-4xl">From diagnosis to recommendation — one accountable partner</h2>
          <div className="mt-12 grid gap-4 md:grid-cols-4">
            {ENGAGEMENT_STEPS.map((s) => (
              <div key={s.step} data-testid={`engagement-step-${s.step}`} className="card relative overflow-hidden">
                <div className="font-mono text-xs text-[var(--teal)]">{s.step}</div>
                <h3 className="mt-3 text-xl font-semibold text-[var(--navy)]">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="eyebrow">Capabilities</div>
            <h2 className="mt-3 text-2xl font-bold text-[var(--navy)] sm:text-3xl lg:text-4xl">Ten capabilities behind every stage</h2>
          </div>
        </div>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.slug];
            return (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="card group !p-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--teal-soft)]/60 text-[var(--teal)] transition-colors duration-200 group-hover:bg-[var(--teal)] group-hover:text-white"><Icon size={18} /></div>
                <h3 className="mt-3 text-sm font-semibold leading-snug text-[var(--navy)]">{s.name}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{s.short}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[var(--teal)]">Explore <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-1" /></span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="grain relative bg-[var(--navy-2)] text-white" data-testid="home-engines">
        <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:items-center lg:px-8">
          <div className="lg:col-span-7">
            <div className="eyebrow !text-teal-300">AI-assisted, consultant-led — where it adds value</div>
            <h2 className="mt-3 text-2xl font-bold sm:text-3xl">Some questions deserve deeper analysis. For those, our consultants draw on in-house AI tools.</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-300">Not every project needs them. Where they strengthen a recommendation — an HTA or IRA readiness check, a patient- or HCP-voice analysis, a competitive scenario model — we apply them inside our own workflows. What you receive is the same in every case: a strategic report with clear insights and recommendations.</p>
          </div>
          <div className="flex flex-wrap gap-2 lg:col-span-5 lg:justify-end">
            {SERVICES.filter((s) => s.intelligence?.length).map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}#intelligence`} data-testid={`engine-link-${s.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-semibold text-slate-200 transition-colors duration-150 hover:bg-white/15"><Lock size={11} className="text-teal-300" /> {s.name}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="eyebrow">Thought leadership</div>
            <h2 className="mt-3 text-2xl font-bold text-[var(--navy)] sm:text-3xl lg:text-4xl">We publish what we practise</h2>
          </div>
          <Link to="/publications" data-testid="home-publications-link" className="btn-navy">View all {pubTotal} publications <ArrowRight size={15} /></Link>
        </div>
        <div className="mt-8 rounded-xl border border-slate-200 bg-white px-2 sm:px-4">
          {pubs.map((p) => <PublicationCard key={p.id} pub={p} compact />)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="grain relative overflow-hidden rounded-2xl bg-[var(--teal)] px-8 py-14 text-white sm:px-14">
          <div className="relative grid gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-8">
              <h2 className="text-2xl font-bold sm:text-3xl lg:text-4xl">Facing an HTA submission, a launch or an evidence gap?</h2>
              <p className="mt-4 text-base text-teal-50">Start with a 45-minute strategy conversation. We'll tell you candidly what evidence you need — and what you don't.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:col-span-4 lg:justify-end">
              <Link to="/contact" data-testid="home-bottom-cta" className="btn-navy">Talk to a consultant <ArrowRight size={15} /></Link>
              <Link to="/academy" data-testid="home-academy-cta" className="btn-outline !border-white/60 !text-white hover:!bg-white hover:!text-[var(--teal)]">MarksMan Academy</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
